// Client-side export helpers: turn the graph and computed payday moves into
// downloadable files. Money is rendered as decimal units for spreadsheets.
import { convertMinor, isPaid, type Flow, type Move, type VulpariNode } from "@vulpari/core";

import { penceToPoundsInput } from "./money.js";

/** Node types that can be ticked off as paid. */
const PAYABLE = new Set<VulpariNode["type"]>(["bill", "card", "oneoff", "pot", "external"]);

function csvField(value: string | number): string {
  return `"${String(value).replace(/"/g, '""')}"`;
}

/** The full graph as pretty JSON, for backup or sharing. */
export function graphToJson(nodes: VulpariNode[], flows: Flow[]): string {
  return JSON.stringify({ nodes, flows }, null, 2);
}

export interface CsvOptions {
  /** The month the moves are for, YYYY-MM, used to date them. */
  month: string;
  /** Currency the amounts are reported in (the ledger's). */
  primaryCurrency: string;
  /** Exchange rates for converting any non-primary amounts. */
  rates?: Record<string, number>;
}

/** The due date of a move, YYYY-MM-DD, or "" when it has no fixed day. */
function dueDateFor(move: Move, nodeById: Map<string, VulpariNode>, month: string): string {
  const day = (value: number) => `${month}-${String(value).padStart(2, "0")}`;
  const source = nodeById.get(move.fromNodeId);
  const target = nodeById.get(move.toNodeId);
  if (source?.type === "income") {
    return day(source.meta.payDay);
  }
  if (target?.type === "bill" || target?.type === "card") {
    return day(target.meta.dueDay);
  }
  if (target?.type === "oneoff") {
    return target.meta.dueDate;
  }
  return "";
}

/** The ordered payday moves as CSV: in/out columns and due dates, amounts in the
 * ledger's currency. Income arriving counts as "in"; everything leaving its
 * source is "out" (including a transfer between your own accounts). */
export function movesToCsv(moves: Move[], nodes: VulpariNode[], options: CsvOptions): string {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const header = ["From", "To", "Due date", "In", "Out", "Paid", "Label"].map(csvField).join(",");
  const rows = moves.map((move) => {
    const source = nodeById.get(move.fromNodeId);
    const target = nodeById.get(move.toNodeId);
    const amount = convertMinor(
      move.amount,
      source?.currency ?? options.primaryCurrency,
      options.primaryCurrency,
      options.rates,
    );
    const formatted = penceToPoundsInput(amount);
    const isIn = source?.type === "income";
    const paidCol =
      target && PAYABLE.has(target.type) ? (isPaid(target, options.month) ? "Yes" : "No") : "";
    return [
      source?.name ?? move.fromNodeId,
      target?.name ?? move.toNodeId,
      dueDateFor(move, nodeById, options.month),
      isIn ? formatted : "",
      isIn ? "" : formatted,
      paidCol,
      move.label,
    ]
      .map(csvField)
      .join(",");
  });
  return [header, ...rows].join("\n");
}

/** Trigger a browser download of text content. */
export function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
